import { BiDashboardExportView, BiExportStatus } from '@euler/model/bi/export';
import { useServiceFactory } from '@euler/services/factory';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { biErrorMessage } from '../functions/biErrors';

const kPage = 20;
/** While an export is queued or running, it is read again this often. */
const kActivePollMs = 5000;
/**
 * A download link is signed for an hour; one read longer ago than this is
 * read again before it is opened.
 */
const kLinkFreshMs = 50 * 60 * 1000;

export type BiExportsState =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | {
      kind: 'ready';
      items: BiDashboardExportView[];
      hasMore: boolean;
      loadingMore: boolean;
    };

function active(item: BiDashboardExportView): boolean {
  return (
    item.status === BiExportStatus.Queued ||
    item.status === BiExportStatus.Running
  );
}

/**
 * The member's exports, newest first, a page at a time. The first page is
 * read again whenever the page comes into view; while it is in view, each
 * queued or running export is read again every few seconds, on whatever
 * page it is, so it turns to 下载 without a pull.
 */
export function useBiExports() {
  const { biService } = useServiceFactory();
  const focused = useIsFocused();
  const [state, setState] = useState<BiExportsState>({ kind: 'loading' });
  // A reload drops pages a slower earlier load returns.
  const generation = useRef(0);
  // When each row was last read: its download link expires an hour later.
  const readAt = useRef(new Map<string, number>());
  const loadingMore = useRef(false);

  const remember = (items: BiDashboardExportView[]) => {
    const now = Date.now();
    for (const item of items) readAt.current.set(item.id, now);
  };

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    loadingMore.current = false;
    try {
      const page = await biService.listDashboardExports({ limit: kPage });
      if (mine !== generation.current) return;
      remember(page.items);
      setState({
        kind: 'ready',
        items: page.items,
        hasMore: page.hasMore,
        loadingMore: false,
      });
    } catch (error) {
      console.error('BI exports failed to load', error);
      if (mine !== generation.current) return;
      // A list already on screen stays; only a first load shows the error.
      setState(current =>
        current.kind === 'ready'
          ? current
          : {
              kind: 'failed',
              message: biErrorMessage(error, '导出记录没有加载出来。'),
            },
      );
    }
  }, [biService]);

  const loadMore = useCallback(async () => {
    if (state.kind !== 'ready' || !state.hasMore || loadingMore.current) {
      return;
    }
    const last = state.items[state.items.length - 1];
    if (!last) return;
    loadingMore.current = true;
    const mine = generation.current;
    setState({ ...state, loadingMore: true });
    try {
      const page = await biService.listDashboardExports({
        beforeId: last.id,
        limit: kPage,
      });
      if (mine !== generation.current) return;
      remember(page.items);
      setState(current =>
        current.kind === 'ready'
          ? {
              kind: 'ready',
              items: [...current.items, ...page.items],
              hasMore: page.hasMore,
              loadingMore: false,
            }
          : current,
      );
    } catch (error) {
      console.error('BI exports page failed to load', error);
      setState(current =>
        current.kind === 'ready' ? { ...current, loadingMore: false } : current,
      );
    } finally {
      if (mine === generation.current) loadingMore.current = false;
    }
  }, [biService, state]);

  /** Puts rows read again in place of theirs, wherever they are. */
  const replace = useCallback((fresh: BiDashboardExportView[]) => {
    if (!fresh.length) return;
    remember(fresh);
    const byId = new Map(fresh.map(item => [item.id, item]));
    setState(current =>
      current.kind === 'ready'
        ? {
            ...current,
            items: current.items.map(item => byId.get(item.id) ?? item),
          }
        : current,
    );
  }, []);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload]),
  );

  const activeIds =
    state.kind === 'ready'
      ? state.items
          .filter(active)
          .map(item => item.id)
          .join(',')
      : '';
  useEffect(() => {
    if (!focused || !activeIds) return;
    const ids = activeIds.split(',');
    const timer = setInterval(() => {
      void Promise.all(
        ids.map(async id => {
          try {
            return await biService.getDashboardExport(id);
          } catch (error) {
            console.error('BI export failed to refresh', error);
            return null;
          }
        }),
      ).then(items =>
        replace(items.filter((x): x is BiDashboardExportView => x !== null)),
      );
    }, kActivePollMs);
    return () => clearInterval(timer);
  }, [focused, activeIds, biService, replace]);

  /** The row's download link, read again first when it may have expired. */
  const downloadUrl = useCallback(
    async (item: BiDashboardExportView): Promise<string | null> => {
      const at = readAt.current.get(item.id) ?? 0;
      if (Date.now() - at < kLinkFreshMs) return item.downloadUrl;
      const fresh = await biService.getDashboardExport(item.id);
      replace([fresh]);
      return fresh.downloadUrl;
    },
    [biService, replace],
  );

  return { state, reload, loadMore, downloadUrl };
}
